import React, { useState, useEffect, useCallback } from 'react';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useNavigate, Link } from 'react-router-dom';
import {
  Building2, Car, Calendar, MapPin, Euro, FileText,
  LogOut, Plus, Users, X, ChevronDown,
  RefreshCw, Activity, Clock3, TrendingUp,
  CheckCircle2, ArrowRight, Download, Mail,
  BarChart3, Wallet
} from 'lucide-react';
import axios from 'axios';

const API_URL = (import.meta.env.VITE_CORPORATE_URL || import.meta.env.VITE_BACKEND_URL) + '/api';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const bookingStatusMeta = {
  pending:         { label: 'En attente',  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  confirmed:       { label: 'Confirmé',    pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  assigned:        { label: 'Assigné',     pill: 'bg-violet-100 text-violet-700 border border-violet-200' },
  accepted:        { label: 'Accepté',     pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200' },
  driver_arriving: { label: 'En route',    pill: 'bg-sky-100 text-sky-700 border border-sky-200' },
  in_progress:     { label: 'En cours',    pill: 'bg-teal-100 text-teal-700 border border-teal-200' },
  completed:       { label: 'Terminé',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  cancelled:       { label: 'Annulé',      pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

const StatusPill = ({ status }) => {
  const meta = bookingStatusMeta[status] || { label: status || '—', pill: 'bg-slate-100 text-slate-700 border border-slate-200' };
  return <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', meta.pill)}>{meta.label}</span>;
};

const SectionCard = ({ title, icon: Icon, iconClass = 'bg-slate-100 text-slate-600', action, children }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
    <div className="mb-5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', iconClass)}>
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-[18px] font-semibold text-slate-900">{title}</h3>
      </div>
      {action && <div>{action}</div>}
    </div>
    {children}
  </div>
);

const EmptyState = ({ icon: Icon, title, desc }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-4 opacity-25"><Icon className="h-12 w-12 text-slate-400" /></div>
    <p className="text-[15px] font-semibold text-slate-700">{title}</p>
    {desc && <p className="mt-1 text-sm text-slate-400 max-w-xs">{desc}</p>}
  </div>
);

const CorporateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [account, setAccount] = useState(null);
  const [rides, setRides] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ first_name: '', last_name: '', email: '', department: '', monthly_budget: '' });

  const token = localStorage.getItem('employeeToken') || localStorage.getItem('clientToken');
  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/company/login'); return; }
    try {
      const meRes = await axios.get(`${API_URL}/employees/me`, { headers: getHeaders() });
      setEmployee(meRes.data);
      if (meRes.data.corporate_account_id) {
        const [dashRes, empsRes] = await Promise.all([
          axios.get(`${API_URL}/corporate/${meRes.data.corporate_account_id}/dashboard`, { headers: getHeaders() }).catch(() => ({ data: { account: null, rides: [] } })),
          axios.get(`${API_URL}/corporate/${meRes.data.corporate_account_id}/employees`, { headers: getHeaders() }).catch(() => ({ data: [] })),
        ]);
        setAccount(dashRes.data.account);
        setRides(dashRes.data.rides || []);
        setEmployees(Array.isArray(empsRes.data) ? empsRes.data : []);
      }
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('employeeToken'); navigate('/company/login'); }
    } finally { setLoading(false); }
  }, [token, navigate, getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addEmployee = async () => {
    if (!newEmployee.email || !newEmployee.first_name || !newEmployee.last_name) { alert('Prénom, nom et email requis.'); return; }
    try {
      await axios.post(`${API_URL}/corporate/${employee.corporate_account_id}/employees`, {
        ...newEmployee,
        monthly_budget: newEmployee.monthly_budget ? parseFloat(newEmployee.monthly_budget) : null
      }, { headers: getHeaders() });
      setNewEmployee({ first_name: '', last_name: '', email: '', department: '', monthly_budget: '' });
      setShowAddEmployee(false);
      fetchData();
      alert('Invitation envoyée !');
    } catch(e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteEmployee = async (empId, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try { await axios.delete(`${API_URL}/employees/${empId}`, { headers: getHeaders() }); fetchData(); }
    catch(e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const resendInvite = async (empId) => {
    try {
      await axios.post(`${API_URL}/corporate/${employee.corporate_account_id}/employees/${empId}/resend-invitation`, {}, { headers: getHeaders() });
      alert('Invitation renvoyée !');
    } catch(e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const handleLogout = () => {
    ['employeeToken','clientToken','employeeInfo','clientInfo'].forEach(k => localStorage.removeItem(k));
    navigate('/company/login');
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-4 text-slate-700 shadow-sm">Chargement...</div>
    </div>
  );

  const tabs = [
    { value: 'dashboard', label: 'Dashboard',  icon: Activity },
    { value: 'rides',     label: 'Courses',    icon: Car },
    { value: 'employees', label: 'Employés',   icon: Users },
    { value: 'invoices',  label: 'Factures',   icon: FileText },
  ];

  const budgetPct = account?.max_monthly_budget
    ? Math.min(100, ((account.current_balance || 0) / account.max_monthly_budget) * 100)
    : null;

  const activeEmployees = employees.filter(e => e.status === 'active').length;
  const pendingEmployees = employees.filter(e => e.status === 'pending').length;

  return (
    <div className="min-h-screen bg-slate-100/80">
      <Seo {...seo.company} />

      {/* TOP BAR */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[radial-gradient(circle_at_top,#1d2550_0%,#131a34_35%,#0f172a_100%)] text-white">
        <div className="mx-auto max-w-[1280px] px-6">
          <div className="flex min-h-[72px] items-center gap-4">
            <div className="min-w-fit">
              <div className="text-[18px] font-bold tracking-tight">BLACK MATTE</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Corporate</div>
            </div>
            <div className="ml-auto flex items-center gap-2 md:gap-3">
              <button onClick={fetchData} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white hover:bg-white/10 transition">
                <RefreshCw className="h-4 w-4" />
              </button>
              <Link to="/reserver" className="inline-flex h-10 items-center gap-2 rounded-xl bg-cyan-500 px-4 text-sm font-semibold text-white hover:bg-cyan-600 transition">
                <Plus className="h-4 w-4" />Nouvelle réservation
              </Link>
              <div className="hidden items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2 md:flex">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-sm font-bold">
                  {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                </div>
                <div className="leading-tight">
                  <div className="text-sm font-medium">{employee?.first_name} {employee?.last_name}</div>
                  <div className="text-xs text-slate-400">{account?.name || employee?.corporate_account_name}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </div>
              <button onClick={handleLogout} className="inline-flex h-10 items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 text-sm font-medium text-white hover:bg-white/10 transition">
                <LogOut className="h-4 w-4" />Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-5 py-5 md:px-6 md:py-6">

        {/* COMPANY BANNER */}
        <div className="mb-5 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 text-xl font-bold flex-shrink-0">
                {(account?.name || employee?.corporate_account_name)?.slice(0,2).toUpperCase() || 'BM'}
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900">{account?.name || employee?.corporate_account_name}</div>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {account?.siret && <span className="text-xs text-slate-400">SIRET: {account.siret}</span>}
                  {account?.negotiated_discount > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      -{(account.negotiated_discount * 100).toFixed(0)}% remise négociée
                    </span>
                  )}
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Compte actif</span>
                </div>
              </div>
            </div>
            {budgetPct !== null && (
              <div className="min-w-[240px]">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Budget mensuel</span>
                  <span className="font-semibold text-slate-700">{(account.current_balance || 0).toFixed(0)} € / {account.max_monthly_budget} €</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                  <div className={cn('h-2 rounded-full transition-all',
                    budgetPct >= 90 ? 'bg-rose-500' : budgetPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500')}
                    style={{ width: `${budgetPct}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* TABS */}
        <div className="mb-5 flex h-auto flex-wrap gap-2 rounded-[20px] border border-slate-200 bg-white p-2 shadow-sm">
          {tabs.map(({ value, label, icon: Icon }) => (
            <button key={value} onClick={() => setActiveTab(value)}
              className={cn('inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition',
                activeTab === value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100')}>
              <Icon className="h-4 w-4" />{label}
              {value === 'employees' && pendingEmployees > 0 && (
                <span className="ml-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{pendingEmployees}</span>
              )}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {activeTab === 'dashboard' && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { title: 'Courses totales', value: account?.total_rides || 0, icon: Car, iconTone: 'bg-blue-100 text-blue-600' },
                { title: 'Total dépensé', value: `${(account?.total_spent || 0).toFixed(2)} €`, icon: Euro, iconTone: 'bg-emerald-100 text-emerald-600' },
                { title: 'En cours / confirmées', value: rides.filter(r => ['pending','confirmed','assigned','in_progress'].includes(r.status)).length, icon: Clock3, iconTone: 'bg-amber-100 text-amber-600' },
                { title: 'Employés actifs', value: `${activeEmployees} / ${employees.length}`, icon: Users, iconTone: 'bg-violet-100 text-violet-600' },
              ].map(({ title, value, icon: Icon, iconTone }) => (
                <div key={title} className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm hover:-translate-y-[1px] transition">
                  <div className="flex items-center gap-3">
                    <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', iconTone)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">{title}</div>
                      <div className="text-[22px] font-bold text-slate-900 leading-tight">{value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <SectionCard title="Courses récentes" icon={Car} iconClass="bg-blue-100 text-blue-600"
              action={<button onClick={() => setActiveTab('rides')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">Voir tout <ArrowRight className="h-4 w-4" /></button>}>
              {rides.length === 0 ? <EmptyState icon={Car} title="Aucune course" desc="Vos réservations corporate apparaîtront ici." /> : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="w-full border-collapse text-sm">
                    <thead><tr className="border-b border-slate-200 bg-slate-50">
                      {['Date','Trajet','Passager','Statut','Montant'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {rides.slice(0,5).map(ride => (
                        <tr key={ride.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                          <td className="px-4 py-3.5 text-slate-700">{ride.date}<div className="text-[11px] text-slate-400">{ride.time}</div></td>
                          <td className="px-4 py-3.5"><div className="font-medium text-slate-900">{ride.pickup_address?.substring(0,28)}...</div><div className="text-[11px] text-slate-400 mt-0.5">→ {ride.destination_address?.substring(0,28)}...</div></td>
                          <td className="px-4 py-3.5 text-slate-600">{ride.passenger_name || '—'}</td>
                          <td className="px-4 py-3.5"><StatusPill status={ride.status} /></td>
                          <td className="px-4 py-3.5 font-semibold text-slate-900">{ride.final_price != null ? `${ride.final_price} €` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Équipe" icon={Users} iconClass="bg-violet-100 text-violet-600"
              action={<button onClick={() => setActiveTab('employees')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">Gérer <ArrowRight className="h-4 w-4" /></button>}>
              {employees.length === 0 ? <EmptyState icon={Users} title="Aucun employé" desc="Invitez des membres de votre équipe." /> : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {employees.slice(0,6).map(emp => (
                    <div key={emp.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-amber-400 flex-shrink-0">{emp.first_name?.[0]}{emp.last_name?.[0]}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{emp.first_name} {emp.last_name}</div>
                        <div className="text-[11px] text-slate-400 truncate">{emp.department || emp.email}</div>
                      </div>
                      <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold flex-shrink-0', emp.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                        {emp.status === 'active' ? 'Actif' : 'Invité'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* COURSES */}
        {activeTab === 'rides' && (
          <SectionCard title="Toutes les courses" icon={Car} iconClass="bg-blue-100 text-blue-600">
            {rides.length === 0 ? <EmptyState icon={Car} title="Aucune course" /> : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full border-collapse text-sm">
                  <thead><tr className="border-b border-slate-200 bg-slate-50">
                    {['Date','Trajet','Passager','Statut','Montant'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {rides.map(ride => (
                      <tr key={ride.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3.5 text-slate-700">{ride.date}<div className="text-[11px] text-slate-400">{ride.time}</div></td>
                        <td className="px-4 py-3.5"><div className="font-medium text-slate-900">{ride.pickup_address?.substring(0,30)}...</div><div className="text-[11px] text-slate-400 mt-0.5">→ {ride.destination_address?.substring(0,30)}...</div></td>
                        <td className="px-4 py-3.5 text-slate-600">{ride.passenger_name || '—'}</td>
                        <td className="px-4 py-3.5"><StatusPill status={ride.status} /></td>
                        <td className="px-4 py-3.5 font-semibold text-slate-900">{ride.final_price != null ? `${ride.final_price} €` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        )}

        {/* EMPLOYES */}
        {activeTab === 'employees' && (
          <SectionCard title="Employés autorisés" icon={Users} iconClass="bg-violet-100 text-violet-600"
            action={
              <button onClick={() => setShowAddEmployee(!showAddEmployee)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition">
                <Plus className="h-4 w-4" />Ajouter
              </button>
            }>
            <div className="mb-4 flex items-center gap-3 text-sm text-slate-500">
              <span><span className="font-semibold text-slate-900">{employees.length}</span> employé{employees.length !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span><span className="font-semibold text-emerald-600">{activeEmployees}</span> actif{activeEmployees !== 1 ? 's' : ''}</span>
              {pendingEmployees > 0 && <><span>·</span><span><span className="font-semibold text-amber-600">{pendingEmployees}</span> en attente d'invitation</span></>}
              <span>·</span>
              <span>Max <span className="font-semibold text-slate-700">50</span> employés</span>
            </div>

            {showAddEmployee && (
              <div className="mb-5 rounded-2xl border border-violet-200 bg-violet-50/40 p-5">
                <div className="text-sm font-semibold text-slate-800 mb-4">Nouvel employé</div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div><label className="mb-1 block text-[11px] font-medium text-slate-500">Prénom *</label><input type="text" value={newEmployee.first_name} onChange={e => setNewEmployee(p => ({...p, first_name: e.target.value}))} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" /></div>
                  <div><label className="mb-1 block text-[11px] font-medium text-slate-500">Nom *</label><input type="text" value={newEmployee.last_name} onChange={e => setNewEmployee(p => ({...p, last_name: e.target.value}))} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" /></div>
                  <div className="col-span-2"><label className="mb-1 block text-[11px] font-medium text-slate-500">Email *</label><input type="email" value={newEmployee.email} onChange={e => setNewEmployee(p => ({...p, email: e.target.value}))} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" /></div>
                  <div><label className="mb-1 block text-[11px] font-medium text-slate-500">Département</label><input type="text" value={newEmployee.department} onChange={e => setNewEmployee(p => ({...p, department: e.target.value}))} placeholder="ex: Direction, RH..." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" /></div>
                  <div><label className="mb-1 block text-[11px] font-medium text-slate-500">Budget mensuel (€)</label><input type="number" min="0" step="10" value={newEmployee.monthly_budget} onChange={e => setNewEmployee(p => ({...p, monthly_budget: e.target.value}))} placeholder="Illimité si vide" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" /></div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAddEmployee(false)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">Annuler</button>
                  <button onClick={addEmployee} className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700">Envoyer invitation</button>
                </div>
              </div>
            )}

            {employees.length === 0 ? <EmptyState icon={Users} title="Aucun employé" desc="Invitez des membres de votre équipe pour gérer leurs réservations." /> : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full border-collapse text-sm">
                  <thead><tr className="border-b border-slate-200 bg-slate-50">
                    {['Employé','Email','Département','Rôle','Statut','Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-amber-400 flex-shrink-0">{emp.first_name?.[0]}{emp.last_name?.[0]}</div>
                            <div>
                              <div className="font-semibold text-slate-900">{emp.first_name} {emp.last_name}</div>
                              {emp.monthly_budget && <div className="text-[11px] text-slate-400">{(emp.current_month_spending || 0).toFixed(0)} / {emp.monthly_budget} €</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-slate-600">{emp.email}</td>
                        <td className="px-4 py-3.5 text-slate-600">{emp.department || '—'}</td>
                        <td className="px-4 py-3.5 text-slate-600">Employé</td>
                        <td className="px-4 py-3.5">
                          <span className={cn('rounded-full px-2.5 py-1 text-[11px] font-semibold',
                            emp.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            emp.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500')}>
                            {emp.status === 'active' ? 'Actif' : emp.status === 'pending' ? 'En attente' : emp.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5">
                            {emp.status === 'pending' && (
                              <button onClick={() => resendInvite(emp.id)} className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100">Renvoyer</button>
                            )}
                            <button onClick={() => deleteEmployee(emp.id, `${emp.first_name} ${emp.last_name}`)} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        )}

        {/* FACTURES */}
        {activeTab === 'invoices' && (
          <SectionCard title="Factures" icon={FileText} iconClass="bg-slate-100 text-slate-600">
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-slate-200 bg-slate-50">
                  {['N° Facture','Période','Courses','Montant TTC','Statut','Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-3 opacity-40">
                        <FileText className="h-10 w-10 text-slate-400" />
                        <div className="text-sm font-medium text-slate-600">Aucune facture disponible</div>
                        <div className="text-xs text-slate-400">Les factures corporate apparaîtront ici après facturation mensuelle</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

      </main>
    </div>
  );
};

export default CorporateDashboard;
